import { useEffect, useId, useState } from "react";
import { Input } from "@salesapp/ui";
import { opsApi } from "../../lib/opsApi";
import type { HintOption } from "../../help/fieldHints";
import { ACTION_HINTS, optionMeaning, withOptions } from "../../help/fieldHints";

export function SearchBar({
  value,
  onChange,
  placeholder,
  hint,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  hint: string;
  label: string;
}) {
  const hintId = useId();
  return (
    <div className="crm-search-wrap">
      <Input
        className="crm-search crm-search--wide"
        type="search"
        autoComplete="off"
        placeholder={placeholder}
        aria-label={label}
        aria-describedby={hintId}
        title={hint}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="crm-search-hint" id={hintId}>
        {hint}
      </p>
    </div>
  );
}

export function optionsList(options: unknown, fallback: string[]): string[] {
  if (Array.isArray(options) && options.length > 0) {
    return options.map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object" && "value" in item) {
        return String((item as { value: unknown }).value);
      }
      return String(item);
    });
  }
  if (typeof options === "string" && options.trim()) {
    return options.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return fallback;
}

/** "pre_qualified" and "Pre qualified" both read "Pre Qualified", so one status is never spelled two ways. */
export function statusLabel(value: string): string {
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function optionsCsv(options: unknown): string {
  return optionsList(options, []).join(", ");
}

export function StatusFilter({
  value,
  onChange,
  options,
  label = "Filter by status",
  optionHints,
  title,
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  label?: string;
  /** When set, each option and the control show what the status means. */
  optionHints?: HintOption[];
  title?: string;
}) {
  const controlTitle =
    title ??
    (optionHints ? withOptions("Show only records in this status.", optionHints) : undefined);
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="crm-select crm-toolbar-filter"
      aria-label={label}
      title={controlTitle}
    >
      <option value="" title={ACTION_HINTS.filter_all_statuses}>
        All statuses
      </option>
      {options.map((status) => (
        <option
          key={status}
          value={status}
          title={optionHints ? optionMeaning(optionHints, status) : undefined}
        >
          {statusLabel(status)}
        </option>
      ))}
    </select>
  );
}

/**
 * One-click status slicer, styled like the grouping toggle. Replaces the "All statuses" dropdown:
 * "All" clears the filter, every other button selects exactly one status.
 */
export function StatusSlicer({
  value,
  onChange,
  options,
  label = "Filter by status",
  optionHints,
  title,
  labelFor = statusLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  label?: string;
  optionHints?: HintOption[];
  title?: string;
  labelFor?: (value: string) => string;
}) {
  return (
    <div className="crm-view-toggle crm-status-slicer" role="group" aria-label={label} title={title}>
      <button
        type="button"
        className={value === "" ? "is-active" : ""}
        aria-pressed={value === ""}
        title={ACTION_HINTS.filter_all_statuses}
        onClick={() => onChange("")}
      >
        All
      </button>
      {options.map((status) => (
        <button
          key={status}
          type="button"
          className={value === status ? "is-active" : ""}
          aria-pressed={value === status}
          title={optionHints ? optionMeaning(optionHints, status) : undefined}
          onClick={() => onChange(value === status ? "" : status)}
        >
          {labelFor(status)}
        </button>
      ))}
    </div>
  );
}

export function useSelectOptions(orgId: string, entity: string, fieldKey: string, fallback: string[]) {
  const [options, setOptions] = useState(fallback);
  useEffect(() => {
    let cancelled = false;
    void opsApi
      .listFields(orgId, entity)
      .then((catalogue) => {
        if (cancelled) return;
        const field = catalogue.fields.find((item) => item.field_key === fieldKey);
        setOptions(optionsList(field?.options, fallback));
      })
      .catch(() => {
        if (!cancelled) setOptions(fallback);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, entity, fieldKey, fallback]);
  return options;
}
